import { z } from 'zod'

import { idSchema, msTimestampSchema } from './common.ts'

// RAL-90: bounded, cited, evidence-first recommendations, per RAL-85's product contract.
// Nothing here is persisted — both a Recommendation and a VideoEvidenceState are computed fresh
// from PublishObservation/PostOutcome/Video each call, same "derived and disposable" posture as
// RAL-88's AccountReachMemory.

// RAL-105 board C's five evidence states. `analysis_failure` has no producer anywhere in this
// codebase yet — no content-analysis pipeline exists (RAL-86/RAL-92 are still in progress) — so
// it is defined here for schema completeness but the classifier in
// backend/services/learning/recommendations.ts never emits it today.
export const EvidenceStateEnum = z.enum([
  'insufficient_evidence',
  'private_excluded',
  'pending_outcome',
  'analysis_failure',
  'outcome_collection_failure'
])
export type EvidenceState = z.infer<typeof EvidenceStateEnum>

export const VideoEvidenceStateSchema = z.object({
  videoId: idSchema,
  state: EvidenceStateEnum,
  // Human-readable, never a bare code — RAL-105 rule: "pending and failed must not share a
  // treatment," each state names its own effect on learning.
  detail: z.string().min(1),
  // Set only for 'pending_outcome' — the real horizon due time, so a UI can render an actual
  // date (RAL-110 rule #11: "names when the fifth result lands") instead of parsing `detail`.
  dueAt: msTimestampSchema.nullable()
})
export type VideoEvidenceState = z.infer<typeof VideoEvidenceStateSchema>

export const RecommendationDimensionEnum = z.enum(['archetype', 'timeBin', 'hashtag'])
export type RecommendationDimension = z.infer<typeof RecommendationDimensionEnum>

export const CitedPostSchema = z.object({
  videoId: idSchema,
  observedAt: msTimestampSchema,
  views: z.number().nonnegative(),
  // views / the account's own rolling median at computation time — never a percentage, never an
  // absolute target, never a cross-account comparison (RAL-105 rule #2).
  reachMultiple: z.number().nonnegative(),
  aboveMedian: z.boolean()
})
export type CitedPost = z.infer<typeof CitedPostSchema>

// RAL-92 (confirmed content taxonomy) will introduce a real suggested-vs-confirmed distinction.
// Until then every archetype is either creator-set (Asset.type) or absent — there is no
// "suggested" value to contrast against, so this is always 'confirmed' today. Kept as an enum
// (not a bare boolean) so RAL-92 can add 'suggested' as a real third state without a breaking
// shape change.
export const ArchetypeConfirmationEnum = z.enum(['confirmed', 'suggested'])
export type ArchetypeConfirmation = z.infer<typeof ArchetypeConfirmationEnum>

export const RecommendationSchema = z.object({
  // Deterministic, not random — `${accountId}:${dimension}:${key}` — stable across recomputes
  // so a frontend can key a list by it.
  id: z.string().min(1),
  accountId: idSchema,
  computedAt: msTimestampSchema,
  dimension: RecommendationDimensionEnum,
  // The bucket key: an AssetType for 'archetype', a 0-21 start hour for 'timeBin' (as a string),
  // or the hashtag text for 'hashtag'.
  key: z.string().min(1),
  archetypeConfirmation: ArchetypeConfirmationEnum.nullable(), // null for non-archetype dimensions
  // e.g. "4 of 5 confirmed Hook posts cleared your 24-hour median" — deterministic, templated,
  // never LLM-generated (no association-vs-cause language beyond what the template itself says).
  claim: z.string().min(1),
  sampleSize: z.number().int().min(1),
  aboveMedianCount: z.number().int().nonnegative(),
  medianReachMultiple: z.number().nonnegative(),
  // Every post behind the claim, including ones that contradict it — RAL-105 rule #3.
  citedPosts: z.array(CitedPostSchema).min(1),
  // What Rally would use this for, if anything currently does. Null today — RAL-87 (grounding
  // agent decisions in this memory) hasn't shipped, so there is no real consumer yet. Never
  // fabricate a use that doesn't exist.
  usedFor: z.enum(['slot_choice', 'caption_phrasing', 'hashtags', 'creator_guidance']).nullable()
})
export type Recommendation = z.infer<typeof RecommendationSchema>

export const AccountRecommendationsSchema = z.object({
  accountId: idSchema,
  computedAt: msTimestampSchema,
  // True once the account has cleared RAL-85's five-public-outcome floor at all — distinct from
  // any one bucket's own insufficientEvidence, since a bucket can be under-floor while the
  // account overall has plenty of outcomes.
  hasEnoughDataToRecommend: z.boolean(),
  totalPublicOutcomes: z.number().int().nonnegative(),
  recommendations: z.array(RecommendationSchema),
  videoEvidenceStates: z.array(VideoEvidenceStateSchema)
})
export type AccountRecommendations = z.infer<typeof AccountRecommendationsSchema>
